import { mapValues, uniqBy } from 'lodash';
import { ChatState } from '../../store/state';
import { ChatAction, ChatActions } from '../actions/chat.action';
import { getLastMessageToken } from '../../shared/utils/chat-utils';
import { GLYMPSE_PRO_CHAT_ORIGIN } from '../../shared/constants/chat.constants';

const initialState: ChatState = {
  connecting: false,
  connected: false,
  error: null,
  chats: null
};

const countUnreadMessages = (chatChannel, messages) => {
  const lastReadMessage = localStorage.getItem(getLastMessageToken(chatChannel)) || 0;
  return messages?.filter(message => message.sequence_id > lastReadMessage && message.origin !== GLYMPSE_PRO_CHAT_ORIGIN)?.length;
};

const chatReducer = (state: ChatState = initialState, action: ChatAction) => {
  let chatId;
  let messages;
  switch (action.type) {
    case ChatActions.PusherConnect:
      return {
        ...state,
        connected: false,
        connecting: true
      };
    case ChatActions.PusherConnectSuccess:
      return {
        ...state,
        connected: true,
        connecting: false
      };
    case ChatActions.PusherConnectFailed:
      return {
        ...state,
        connecting: false,
        connected: false,
        error: action.payload
      };
    case ChatActions.PusherDisconnect:
      return {
        ...state,
        connecting: false,
        connected: false,
        error: null,
      };
    case ChatActions.SubscribeChannel:
      chatId = action?.payload?.chat_channel;
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            chat_id: chatId,
            chatInfo: action.payload.chatInfo
          }
        }
      };
    case ChatActions.UnsubscribeChannel:
      // eslint-disable-next-line no-case-declarations
      const chats = {
        ...(state?.chats ?? {}),
      };
      // eslint-disable-next-line no-param-reassign
      delete chats?.[action.payload];
      return {
        ...state,
        chats
      };
    case ChatActions.MessageReceived:
      chatId = action?.payload?.chat_channel;
      messages = uniqBy([...(state?.chats?.[chatId]?.messages ?? []), action?.payload?.message], 'id');
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            chat_id: chatId,
            messages,
            unreadMessagesCount: countUnreadMessages(chatId, messages),
            lastSequenceId: action?.payload?.message?.sequence_id
          }
        }
      };
    case ChatActions.LoadMessages:
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [action.payload]: {
            ...(state?.chats?.[action.payload] ?? {}),
            loading: true,
            loaded: false,
            unreadMessagesCount: 0
          }
        }
      };
    case ChatActions.LoadMessagesSuccess:
      chatId = action?.payload?.chat_channel;
      messages = uniqBy([...(state?.chats?.[chatId]?.messages ?? []), ...(action.payload.messages ?? [])], 'id');
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            chat_id: chatId,
            messages,
            loading: false,
            loaded: true,
            unreadMessagesCount: countUnreadMessages(chatId, messages),
            lastSequenceId: messages?.[messages?.length - 1]?.sequence_id
          }
        }
      };
    case ChatActions.LoadMessagesFailed:
      chatId = action?.payload?.chat_channel;
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            loading: false,
            loaded: false,
            error: action.payload.error
          }
        }
      };
    case ChatActions.SendMessage:
      chatId = action?.payload?.chat_channel;
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            sending: true,
            sent: false,
            error: null,
          }
        }
      };
    case ChatActions.SendMessageSuccess:
      chatId = action?.payload?.chat_channel;
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            sending: false,
            sent: true,
            error: null,
            message: ''
          }
        }
      };
    case ChatActions.SendMessageFailed:
      chatId = action?.payload?.chat_channel;
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            sending: false,
            sent: false,
            error: action.payload.error
          }
        }
      };
    case ChatActions.ChatNewMessageUpdate:
      chatId = action?.payload?.chat_channel;
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            message: action.payload.message
          }
        }
      };
    case ChatActions.ReadMessages:
      chatId = action?.payload;
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            unreadMessagesCount: 0
          }
        }
      };
    case ChatActions.ReadAllChats:
      return {
        ...state,
        chats: mapValues(state?.chats, (chat) => ({
          ...(chat ?? {}),
          unreadMessagesCount: 0
        }))
      };
    default:
      return state;
  }
};

export default chatReducer;
